import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { CheckCircleOutline as CheckIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { track } from '../../utils/analytics';

/**
 * What a demo account sees instead of Settings.
 *
 * <p>The server refuses every setting a demo account tries to change, so the honest thing is not
 * to show twenty disabled switches. It is also the wrong page to waste: somebody who opened
 * Settings was looking for their own number, their own answers, their own calendar, which is
 * exactly the list of reasons to have an account. So it says what is behind the door rather
 * than that the door is locked.
 */
const DEMO_UNAVAILABLE = [
  'Your own WhatsApp number, instead of the shared one these demos go out on',
  'Answers delivered to your email, a Google Sheet or your own webhook',
  'Live booking into your calendar, with real availability',
  'Your flows kept, and as many as you need',
];

const DemoSettingsNotice = () => {
  const navigate = useNavigate();

  const createAccount = () => {
    track('signup_started', { source: 'settings_demo_notice' });
    navigate('/login?tab=register');
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Settings come with an account
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        You are building on a shared demo number, so there is nothing here that is yours to
        change yet. Everything you have built stays with you when you sign up.
      </Typography>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            What an account gives you
          </Typography>
          <List dense>
            {DEMO_UNAVAILABLE.map((item) => (
              <ListItem key={item} disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={item} />
              </ListItem>
            ))}
          </List>
          <Button
            variant="contained"
            onClick={createAccount}
            sx={{ mt: 1 }}
            data-testid="settings-create-account"
          >
            Create an account
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DemoSettingsNotice;
